/**
 * Plantillas de email para el demo. Cada `kind` (tipo de email transaccional)
 * tiene varias `variants` con HTML real — imágenes, layouts y styling distintos.
 *
 * El selector de la página es:
 *   list of kinds → select kind → list of variants → select variant → preview iframe
 *
 * `previewHtml(kind, variant)` devuelve el HTML que va al iframe.
 */

interface VariantInfo {
  variant: string
  label: string
  description: string
}

interface KindInfo {
  kind: string
  label: string
  description: string
  defaultVariant: string
  selectedVariant: string
  variants: VariantInfo[]
}

export const EMAIL_TEMPLATES_DEMO: KindInfo[] = [
  {
    kind: 'order_paid_customer',
    label: 'Confirmación de pago — cliente',
    description: 'Se envía al cliente cuando el pago de su orden queda aprobado.',
    defaultVariant: 'minimal',
    selectedVariant: 'editorial',
    variants: [
      { variant: 'minimal',    label: 'Minimalista',           description: 'Sólo tipografía. Negro sobre blanco. Carga ultra rápida.' },
      { variant: 'editorial',  label: 'Editorial con banner',  description: 'Hero con foto del drop, layout tipo revista.' },
      { variant: 'receipt',    label: 'Tipo recibo',           description: 'Tabla de items, totales y código QR de seguimiento.' },
      { variant: 'polaroid',   label: 'Polaroid',              description: 'Fotos del producto en marco vintage + handwritten note.' },
      { variant: 'dark-neon',  label: 'Dark neón',             description: 'Fondo oscuro, acentos cyan/magenta. Para marcas streetwear.' },
    ],
  },
  {
    kind: 'order_paid_admin',
    label: 'Nueva orden pagada — admin',
    description: 'Notificación interna al equipo cuando se confirma una venta.',
    defaultVariant: 'compact',
    selectedVariant: 'compact',
    variants: [
      { variant: 'compact',  label: 'Compacta',  description: 'Una tabla con cliente, monto y link al admin.' },
      { variant: 'detailed', label: 'Detallada', description: 'Items, dirección de envío y método de pago.' },
      { variant: 'slack',    label: 'Estilo Slack', description: 'Block con emoji, datos en línea y CTA pequeño.' },
    ],
  },
  {
    kind: 'order_shipped',
    label: 'Orden enviada',
    description: 'Notifica al cliente que su pedido salió, con link de tracking.',
    defaultVariant: 'minimal',
    selectedVariant: 'tracking-card',
    variants: [
      { variant: 'minimal',       label: 'Minimalista',           description: 'Texto + botón de tracking.' },
      { variant: 'tracking-card', label: 'Card con tracking',     description: 'Hero con camión + número de guía + ETA.' },
      { variant: 'roadmap',       label: 'Roadmap visual',        description: 'Timeline con 4 estados: empacado → en tránsito → reparto → entregado.' },
      { variant: 'product-hero',  label: 'Producto en grande',    description: 'Foto del producto que enviaste, gigante.' },
    ],
  },
  {
    kind: 'welcome',
    label: 'Bienvenida a la cuenta',
    description: 'Para usuarios nuevos que crean cuenta en el storefront.',
    defaultVariant: 'minimal',
    selectedVariant: 'coupon',
    variants: [
      { variant: 'minimal',  label: 'Minimalista',          description: 'Saludo + CTA "Explorar productos".' },
      { variant: 'coupon',   label: 'Con cupón',            description: 'Código de descuento de bienvenida + countdown.' },
      { variant: 'magazine', label: 'Tipo magazine',        description: 'Editorial, pocos colores, tipografía grande.' },
      { variant: 'community', label: 'Comunidad',           description: 'Onboarding con links a IG, Discord y TikTok.' },
    ],
  },
  {
    kind: 'phone_request',
    label: 'Solicitar número de contacto',
    description: 'Para órdenes pagadas sin teléfono — envío masivo con link único.',
    defaultVariant: 'minimal',
    selectedVariant: 'minimal',
    variants: [
      { variant: 'minimal',  label: 'Minimalista', description: 'Mensaje breve + botón único para abrir el form.' },
      { variant: 'urgency',  label: 'Con urgencia', description: 'Banner amarillo "Necesitamos tu teléfono para enviar tu pedido".' },
    ],
  },
  {
    kind: 'size_inquiry',
    label: 'Talla incorrecta — solicitar nueva',
    description: 'Cuando el item del carrito quedó sin variante asignada.',
    defaultVariant: 'minimal',
    selectedVariant: 'minimal',
    variants: [
      { variant: 'minimal', label: 'Minimalista',        description: 'Texto + link de seguimiento para rectificar.' },
      { variant: 'visual',  label: 'Con foto + tabla',   description: 'Imagen del producto + tabla de tallas inline.' },
    ],
  },
  {
    kind: 'abandoned_cart',
    label: 'Carrito abandonado',
    description: 'Recupera ventas: se manda 4h después de que el cliente abandona el checkout.',
    defaultVariant: 'subtle',
    selectedVariant: 'with-discount',
    variants: [
      { variant: 'subtle',         label: 'Sutil',         description: '"¿Te quedaste sin stock?" sin descuento, foco en finalizar.' },
      { variant: 'with-discount',  label: 'Con descuento', description: 'Cupón de 10% para volver a comprar dentro de 24h.' },
      { variant: 'social-proof',   label: 'Social proof',  description: 'Reviews del producto + cantidad de personas viendo.' },
    ],
  },
  {
    kind: 'restock_alert',
    label: 'Producto restock',
    description: 'Cliente avisó interés en producto agotado — notificar cuando vuelve.',
    defaultVariant: 'minimal',
    selectedVariant: 'product-hero',
    variants: [
      { variant: 'minimal',      label: 'Minimalista',        description: 'Texto + CTA "Ver producto".' },
      { variant: 'product-hero', label: 'Producto en grande', description: 'Foto del producto + countdown de stock limitado.' },
    ],
  },
]

// ============================================================
// Preview HTML — variant-specific
// ============================================================

const HERO_IMAGE_BY_KIND: Record<string, string> = {
  order_paid_customer: 'https://picsum.photos/seed/hero-paid/1200/400',
  order_paid_admin:    'https://picsum.photos/seed/hero-admin/1200/400',
  order_shipped:       'https://picsum.photos/seed/hero-shipped/1200/400',
  welcome:             'https://picsum.photos/seed/hero-welcome/1200/400',
  phone_request:       'https://picsum.photos/seed/hero-phone/1200/400',
  size_inquiry:        'https://picsum.photos/seed/hero-size/1200/400',
  abandoned_cart:      'https://picsum.photos/seed/hero-cart/1200/400',
  restock_alert:       'https://picsum.photos/seed/hero-restock/1200/400',
}

const PRODUCT_CARD = `
  <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#f9fafb;border-radius:8px;margin:20px 0">
    <tr>
      <td width="120" style="padding:16px"><img src="https://picsum.photos/seed/ls2/240/240" width="100" height="100" style="border-radius:6px;display:block" alt=""></td>
      <td valign="top" style="padding:16px 16px 16px 0">
        <p style="margin:0;font-weight:600;color:#111;font-size:15px">Long Sleeve "Somnolencia"</p>
        <p style="margin:4px 0 0;color:#6b7280;font-size:12px">Talla XXL · Color crudo</p>
        <p style="margin:8px 0 0;font-weight:600;color:#111">$ 240.000 COP</p>
      </td>
    </tr>
  </table>
`

const SOCIAL_BAR = `
  <table cellpadding="0" cellspacing="0" border="0" align="center" style="margin:24px auto 0">
    <tr>
      <td style="padding:0 8px"><a href="#" style="text-decoration:none;color:#9ca3af;font-size:12px">Instagram</a></td>
      <td style="color:#d1d5db">·</td>
      <td style="padding:0 8px"><a href="#" style="text-decoration:none;color:#9ca3af;font-size:12px">TikTok</a></td>
      <td style="color:#d1d5db">·</td>
      <td style="padding:0 8px"><a href="#" style="text-decoration:none;color:#9ca3af;font-size:12px">Discord</a></td>
    </tr>
  </table>
`

function shell(body: string, opts: { bg?: string; fg?: string } = {}): string {
  const bg = opts.bg ?? '#ffffff'
  const fg = opts.fg ?? '#1f2937'
  return `<!doctype html><html lang="es"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">
  <title>Email preview</title>
  <style>body{margin:0;background:${bg};color:${fg};font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;line-height:1.55}a{color:inherit}</style>
  </head><body>${body}</body></html>`
}

function previewOrderPaidCustomer(variant: string): string {
  if (variant === 'minimal') {
    return shell(`
      <table cellpadding="0" cellspacing="0" border="0" align="center" width="540" style="margin:48px auto;padding:0 24px">
        <tr><td style="text-align:center;padding-bottom:32px"><h1 style="margin:0;font-size:18px;letter-spacing:6px;font-weight:600">DEMO STUDIOS</h1></td></tr>
        <tr><td><h2 style="margin:0 0 12px;font-size:24px;font-weight:600">Tu orden está confirmada.</h2>
          <p style="color:#6b7280;margin:0 0 28px">Hola Sebastián, recibimos tu pago. Ya estamos preparando el pedido.</p>
          <p style="border-top:1px solid #e5e7eb;border-bottom:1px solid #e5e7eb;padding:16px 0;margin:0;display:flex;justify-content:space-between"><span>Total</span><strong>$ 240.000 COP</strong></p>
          <p style="margin:32px 0 0"><a href="#" style="color:#000;text-decoration:underline;font-weight:600">Ver mi orden →</a></p>
        </td></tr>
      </table>
    `)
  }
  if (variant === 'editorial') {
    return shell(`
      <table cellpadding="0" cellspacing="0" border="0" align="center" width="600" style="margin:0 auto;background:#fff">
        <tr><td><img src="${HERO_IMAGE_BY_KIND.order_paid_customer}" width="600" style="display:block;width:100%" alt=""></td></tr>
        <tr><td style="padding:40px 40px 32px">
          <p style="margin:0 0 8px;text-transform:uppercase;letter-spacing:4px;font-size:11px;color:#9ca3af">EUTANASIA · DROP 03</p>
          <h1 style="margin:0;font-size:36px;font-weight:800;line-height:1.1">Hecho. Tu pieza<br/>está en camino.</h1>
          <p style="margin:20px 0 0;color:#374151;font-size:16px">Hola Sebastián. Recibimos el pago. Ahora preparamos tu orden con la curaduría que merece.</p>
        </td></tr>
        <tr><td style="padding:0 40px">${PRODUCT_CARD}</td></tr>
        <tr><td style="padding:0 40px 32px">
          <table cellpadding="0" cellspacing="0" width="100%"><tr>
            <td style="border-top:1px solid #e5e7eb;padding:14px 0;color:#6b7280;font-size:14px">Subtotal</td>
            <td style="border-top:1px solid #e5e7eb;padding:14px 0;text-align:right;font-size:14px">$ 240.000</td></tr><tr>
            <td style="padding:8px 0;color:#6b7280;font-size:14px">Envío</td><td style="padding:8px 0;text-align:right;font-size:14px">Gratis</td></tr><tr>
            <td style="border-top:1px solid #111;padding:14px 0;font-weight:700;font-size:16px">Total</td>
            <td style="border-top:1px solid #111;padding:14px 0;text-align:right;font-weight:700;font-size:16px">$ 240.000 COP</td></tr></table>
        </td></tr>
        <tr><td style="text-align:center;padding:0 40px 40px"><a href="#" style="display:inline-block;background:#111;color:#fff;text-decoration:none;padding:14px 32px;border-radius:6px;font-weight:600">Seguir mi orden</a></td></tr>
        <tr><td style="background:#f9fafb;padding:24px 40px;text-align:center;color:#9ca3af;font-size:12px">Centrio Studios · Bogotá, Colombia</td></tr>
      </table>
    `)
  }
  if (variant === 'receipt') {
    return shell(`
      <table cellpadding="0" cellspacing="0" border="0" align="center" width="520" style="margin:32px auto;background:#fff;border:1px dashed #d1d5db;padding:28px;font-family:'Courier New',monospace">
        <tr><td style="text-align:center;border-bottom:1px dashed #d1d5db;padding-bottom:16px">
          <p style="margin:0;font-size:18px;letter-spacing:8px;font-weight:700">DEMO STUDIOS</p>
          <p style="margin:6px 0 0;font-size:11px;color:#6b7280">RECIBO DE COMPRA · ORD-MOXBTC7H-V4XJ</p>
        </td></tr>
        <tr><td style="padding:16px 0;border-bottom:1px dashed #d1d5db">
          <p style="margin:0;font-size:13px;color:#111">CLIENTE: Sebastián Lozano</p>
          <p style="margin:4px 0;font-size:13px;color:#111">FECHA: 09 MAY 2026 · 14:42</p>
          <p style="margin:4px 0;font-size:13px;color:#111">PAGO: ePayco · PSE</p>
        </td></tr>
        <tr><td style="padding:16px 0;border-bottom:1px dashed #d1d5db">
          <table width="100%" cellpadding="0" cellspacing="0">
            <tr><td style="font-size:13px">1× Long Sleeve "Somnolencia"</td><td style="font-size:13px;text-align:right">$ 240.000</td></tr>
            <tr><td style="padding-left:16px;font-size:11px;color:#6b7280">Talla XXL</td><td></td></tr>
          </table>
        </td></tr>
        <tr><td style="padding:16px 0">
          <table width="100%" cellpadding="0" cellspacing="0">
            <tr><td style="font-size:13px">SUBTOTAL</td><td style="font-size:13px;text-align:right">$ 240.000</td></tr>
            <tr><td style="font-size:13px">ENVÍO</td><td style="font-size:13px;text-align:right">$ 0</td></tr>
            <tr><td style="font-size:15px;font-weight:700;padding-top:8px;border-top:1px solid #111">TOTAL</td>
                <td style="font-size:15px;font-weight:700;padding-top:8px;border-top:1px solid #111;text-align:right">$ 240.000 COP</td></tr>
          </table>
        </td></tr>
        <tr><td style="text-align:center;padding-top:20px"><img src="https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=https://centrio.skeleton-store.com/seguimiento" width="120" alt="QR seguimiento"></td></tr>
        <tr><td style="text-align:center;padding-top:12px;color:#6b7280;font-size:11px">Escanea para ver tu seguimiento</td></tr>
      </table>
    `)
  }
  if (variant === 'polaroid') {
    return shell(`
      <table cellpadding="0" cellspacing="0" border="0" align="center" width="540" style="margin:48px auto;padding:0 24px;text-align:center">
        <tr><td>
          <div style="display:inline-block;background:#fff;padding:16px 16px 64px;box-shadow:0 8px 24px rgba(0,0,0,0.12);transform:rotate(-2deg)">
            <img src="https://picsum.photos/seed/ls2/360/360" width="320" height="320" style="display:block" alt="">
            <p style="font-family:'Caveat','Brush Script MT',cursive;font-size:24px;margin:24px 0 0">Bienvenida a la familia</p>
          </div>
        </td></tr>
        <tr><td style="padding:48px 0 16px"><h2 style="margin:0;font-size:22px;font-weight:600">Sebastián, gracias por tu compra ✨</h2></td></tr>
        <tr><td><p style="color:#6b7280;margin:8px 0 24px">Empacamos cada pieza a mano. La tuya sale mañana.</p></td></tr>
        <tr><td>${PRODUCT_CARD}</td></tr>
        <tr><td><a href="#" style="display:inline-block;background:#111;color:#fff;text-decoration:none;padding:12px 28px;border-radius:999px;font-weight:600;margin-top:8px">Ver mi orden</a></td></tr>
        <tr><td>${SOCIAL_BAR}</td></tr>
      </table>
    `, { bg: '#fefcf7' })
  }
  if (variant === 'dark-neon') {
    return shell(`
      <table cellpadding="0" cellspacing="0" border="0" align="center" width="600" style="margin:0 auto;background:#0a0a0f;color:#f5f5f5">
        <tr><td style="padding:40px 32px 24px">
          <p style="margin:0;font-size:11px;letter-spacing:6px;color:#22d3ee">// CONFIRMED</p>
          <h1 style="margin:8px 0 0;font-size:38px;font-weight:900;background:linear-gradient(90deg,#22d3ee,#e879f9);-webkit-background-clip:text;background-clip:text;color:transparent">PEDIDO PROCESADO</h1>
          <p style="margin:16px 0 0;color:#a1a1aa;font-size:15px">Sebastián — recibimos tu pago. La pieza ya está en cola de empaque.</p>
        </td></tr>
        <tr><td style="padding:0 32px">
          <table cellpadding="0" cellspacing="0" width="100%" style="background:#11131a;border:1px solid #22d3ee33;border-radius:8px;margin:8px 0 24px">
            <tr><td width="100" style="padding:14px"><img src="https://picsum.photos/seed/ls2/180/180" width="80" height="80" style="border-radius:4px;display:block" alt=""></td>
                <td valign="top" style="padding:14px 14px 14px 0">
                  <p style="margin:0;font-weight:700;color:#f5f5f5">Long Sleeve "Somnolencia"</p>
                  <p style="margin:4px 0;color:#71717a;font-size:12px">XXL · Crudo</p>
                  <p style="margin:8px 0 0;color:#22d3ee;font-weight:700">$ 240.000 COP</p>
                </td></tr>
          </table>
          <p style="margin:0;color:#71717a;font-size:12px;text-transform:uppercase;letter-spacing:2px">TOTAL</p>
          <p style="margin:4px 0 24px;font-size:32px;font-weight:900;color:#fff">$ 240.000 <span style="font-size:14px;color:#71717a">COP</span></p>
          <a href="#" style="display:inline-block;border:1px solid #22d3ee;color:#22d3ee;text-decoration:none;padding:14px 28px;font-weight:700;letter-spacing:2px;font-size:13px">RASTREAR ORDEN →</a>
        </td></tr>
        <tr><td style="padding:32px;text-align:center;border-top:1px solid #1f2937;color:#52525b;font-size:11px;letter-spacing:1px">DEMO_STUDIOS · BOGOTÁ_CO · 2026</td></tr>
      </table>
    `, { bg: '#000' })
  }
  return shell(`<p style="padding:32px">Variante "${variant}" no implementada todavía.</p>`)
}

function previewOrderPaidAdmin(variant: string): string {
  if (variant === 'compact') {
    return shell(`
      <table cellpadding="0" cellspacing="0" border="0" align="center" width="520" style="margin:32px auto;background:#fff;border:1px solid #e5e7eb;border-radius:8px;overflow:hidden">
        <tr><td style="background:#10b981;padding:14px 20px;color:#fff;font-weight:700">🟢 Nueva orden pagada · ORD-MOXBTC7H-V4XJ</td></tr>
        <tr><td style="padding:24px"><table width="100%" cellpadding="6">
          <tr><td style="color:#6b7280;width:120px">Cliente</td><td style="font-weight:600">Sebastián Lozano</td></tr>
          <tr><td style="color:#6b7280">Email</td><td>cliente1@gmail.com</td></tr>
          <tr><td style="color:#6b7280">Pasarela</td><td>ePayco · PSE</td></tr>
          <tr><td style="color:#6b7280">Total</td><td style="font-weight:700;font-size:18px;color:#10b981">$ 240.000 COP</td></tr>
        </table></td></tr>
        <tr><td style="background:#f9fafb;padding:14px 24px;text-align:right">
          <a href="#" style="background:#111;color:#fff;text-decoration:none;padding:10px 18px;border-radius:6px;font-size:13px;font-weight:600">Abrir en admin →</a>
        </td></tr>
      </table>
    `)
  }
  if (variant === 'detailed') {
    return shell(`
      <table cellpadding="0" cellspacing="0" border="0" align="center" width="600" style="margin:32px auto;background:#fff">
        <tr><td style="border-bottom:3px solid #10b981;padding:24px 32px"><h2 style="margin:0">🟢 Nueva orden pagada</h2><p style="margin:4px 0 0;color:#6b7280;font-family:monospace;font-size:13px">ORD-MOXBTC7H-V4XJ</p></td></tr>
        <tr><td style="padding:24px 32px">
          <h3 style="margin:0 0 12px;font-size:13px;text-transform:uppercase;letter-spacing:2px;color:#6b7280">Items</h3>${PRODUCT_CARD}
          <h3 style="margin:24px 0 12px;font-size:13px;text-transform:uppercase;letter-spacing:2px;color:#6b7280">Cliente</h3>
          <p style="margin:0">Sebastián Lozano · cliente1@gmail.com · 321 361 3163</p>
          <h3 style="margin:24px 0 12px;font-size:13px;text-transform:uppercase;letter-spacing:2px;color:#6b7280">Envío a</h3>
          <p style="margin:0;color:#374151">Calle 167 #16B-44, Bogotá D.C., 110131</p>
          <h3 style="margin:24px 0 12px;font-size:13px;text-transform:uppercase;letter-spacing:2px;color:#6b7280">Pago</h3>
          <p style="margin:0">ePayco · PSE · ref 365648722</p>
          <p style="margin:32px 0 0;text-align:right;font-size:22px;font-weight:700">Total: $ 240.000 COP</p>
        </td></tr>
        <tr><td style="background:#111;padding:20px 32px;text-align:center"><a href="#" style="color:#fff;text-decoration:none;font-weight:600">Abrir orden en el admin →</a></td></tr>
      </table>
    `)
  }
  if (variant === 'slack') {
    return shell(`
      <table cellpadding="0" cellspacing="0" border="0" align="center" width="500" style="margin:32px auto;background:#fff;border-left:4px solid #10b981;padding:14px 18px">
        <tr><td>
          <p style="margin:0;font-size:14px;font-weight:700">💸 Nueva orden — $ 240.000</p>
          <p style="margin:6px 0 0;color:#6b7280;font-size:13px"><code>ORD-MOXBTC7H-V4XJ</code> · Sebastián L. · ePayco PSE</p>
          <p style="margin:10px 0 0"><a href="#" style="color:#1d4ed8;text-decoration:none;font-size:13px;font-weight:600">Ver detalle</a></p>
        </td></tr>
      </table>
    `, { bg: '#f9fafb' })
  }
  return shell(`<p style="padding:32px">Variante "${variant}" no implementada todavía.</p>`)
}

function previewOrderShipped(variant: string): string {
  if (variant === 'minimal') {
    return shell(`
      <table cellpadding="0" cellspacing="0" border="0" align="center" width="540" style="margin:48px auto;padding:0 24px">
        <tr><td><h2 style="margin:0">📦 Tu orden ya está en camino</h2>
          <p style="color:#6b7280;margin:12px 0 24px">Hola Sebastián, tu pedido salió hoy con Coordinadora.</p>
          <p style="background:#f9fafb;padding:16px;border-radius:6px;margin:0;font-family:monospace">Tracking: <strong>COORDI-998801</strong></p>
          <p style="margin:24px 0 0"><a href="#" style="color:#000;text-decoration:underline;font-weight:600">Rastrear envío →</a></p>
        </td></tr>
      </table>
    `)
  }
  if (variant === 'tracking-card') {
    return shell(`
      <table cellpadding="0" cellspacing="0" border="0" align="center" width="600" style="margin:0 auto">
        <tr><td style="background:#10b981;padding:48px 32px;text-align:center;color:#fff">
          <p style="margin:0;font-size:64px">🚚</p>
          <h1 style="margin:12px 0 0;font-size:28px">Tu orden salió hoy</h1>
          <p style="margin:6px 0 0;opacity:0.85">Llegada estimada: <strong>11–13 mayo</strong></p>
        </td></tr>
        <tr><td style="padding:32px 40px;background:#fff">
          <p style="margin:0;color:#6b7280;font-size:13px;text-transform:uppercase;letter-spacing:2px">Número de guía</p>
          <p style="margin:6px 0 0;font-family:monospace;font-size:22px;font-weight:700">COORDI-998801</p>
          <p style="margin:24px 0 0">Transportadora: <strong>Coordinadora</strong></p>
          <p style="margin:6px 0 24px;color:#6b7280">Dirección: Calle 167 #16B-44, Bogotá</p>
          <a href="#" style="display:inline-block;background:#111;color:#fff;text-decoration:none;padding:14px 32px;border-radius:6px;font-weight:600">Rastrear paquete</a>
        </td></tr>
      </table>
    `)
  }
  if (variant === 'roadmap') {
    return shell(`
      <table cellpadding="0" cellspacing="0" border="0" align="center" width="600" style="margin:32px auto;background:#fff">
        <tr><td style="padding:32px 40px">
          <h2 style="margin:0">Tu orden está en tránsito</h2>
          <p style="color:#6b7280;margin:8px 0 32px">Te avisamos en cada paso del camino.</p>
          <table cellpadding="0" cellspacing="0" width="100%">
            <tr>
              <td align="center" width="25%"><div style="background:#10b981;width:36px;height:36px;border-radius:50%;color:#fff;line-height:36px;font-weight:700;margin:0 auto">✓</div><p style="margin:8px 0 0;font-size:11px;color:#10b981;text-transform:uppercase;font-weight:700">Recibido</p></td>
              <td align="center" width="25%"><div style="background:#10b981;width:36px;height:36px;border-radius:50%;color:#fff;line-height:36px;font-weight:700;margin:0 auto">✓</div><p style="margin:8px 0 0;font-size:11px;color:#10b981;text-transform:uppercase;font-weight:700">Empacado</p></td>
              <td align="center" width="25%"><div style="background:#10b981;width:36px;height:36px;border-radius:50%;color:#fff;line-height:36px;font-weight:700;margin:0 auto">●</div><p style="margin:8px 0 0;font-size:11px;color:#10b981;text-transform:uppercase;font-weight:700">En tránsito</p></td>
              <td align="center" width="25%"><div style="background:#e5e7eb;width:36px;height:36px;border-radius:50%;color:#9ca3af;line-height:36px;font-weight:700;margin:0 auto">·</div><p style="margin:8px 0 0;font-size:11px;color:#9ca3af;text-transform:uppercase">Entregado</p></td>
            </tr>
          </table>
          <p style="margin:32px 0 0;text-align:center;color:#6b7280">Estamos acá: <strong>Centro de distribución Bogotá</strong></p>
          <p style="margin:24px 0 0;text-align:center"><a href="#" style="background:#111;color:#fff;text-decoration:none;padding:12px 28px;border-radius:6px;font-weight:600">Rastrear paquete</a></p>
        </td></tr>
      </table>
    `)
  }
  if (variant === 'product-hero') {
    return shell(`
      <table cellpadding="0" cellspacing="0" border="0" align="center" width="600" style="margin:0 auto;background:#fff">
        <tr><td><img src="https://picsum.photos/seed/ls2/600/600" width="600" style="display:block;width:100%" alt=""></td></tr>
        <tr><td style="padding:32px 40px"><h2 style="margin:0;font-size:30px;font-weight:800">Tu Long Sleeve sale hoy</h2>
          <p style="color:#6b7280;margin:12px 0 24px">Empacada a mano. Tracking adjunto.</p>
          <p style="margin:0;font-family:monospace;background:#f9fafb;padding:14px;border-radius:6px"><strong>COORDI-998801</strong> · Coordinadora · 2-3 días</p>
          <p style="margin:24px 0 0"><a href="#" style="background:#111;color:#fff;text-decoration:none;padding:14px 28px;border-radius:6px;font-weight:600">Seguir en tiempo real</a></p>
        </td></tr>
      </table>
    `)
  }
  return shell(`<p style="padding:32px">Variante "${variant}" no implementada todavía.</p>`)
}

function previewWelcome(variant: string): string {
  if (variant === 'minimal') {
    return shell(`
      <table cellpadding="0" cellspacing="0" border="0" align="center" width="520" style="margin:48px auto;padding:0 24px">
        <tr><td><h2 style="margin:0">Bienvenido, Sebastián</h2>
          <p style="color:#6b7280;margin:12px 0 32px">Tu cuenta está lista. Empezá a explorar el drop.</p>
          <a href="#" style="display:inline-block;background:#111;color:#fff;text-decoration:none;padding:14px 32px;border-radius:6px;font-weight:600">Ver productos</a>
        </td></tr>
      </table>
    `)
  }
  if (variant === 'coupon') {
    return shell(`
      <table cellpadding="0" cellspacing="0" border="0" align="center" width="600" style="margin:0 auto;background:#fff">
        <tr><td style="background:linear-gradient(135deg,#f97316,#ea580c);padding:48px 32px;text-align:center;color:#fff">
          <p style="margin:0;font-size:11px;letter-spacing:6px;opacity:0.85">DROP EUTANASIA</p>
          <h1 style="margin:12px 0 0;font-size:36px;font-weight:900">Bienvenido</h1>
          <p style="margin:8px 0 0;font-size:16px;opacity:0.9">Tu primera compra es con descuento.</p>
        </td></tr>
        <tr><td style="padding:48px 40px;text-align:center">
          <p style="margin:0;color:#6b7280;font-size:13px;text-transform:uppercase;letter-spacing:2px">Tu código</p>
          <div style="display:inline-block;border:2px dashed #f97316;padding:16px 32px;margin:16px 0;border-radius:8px">
            <p style="margin:0;font-size:30px;font-weight:900;font-family:monospace;letter-spacing:4px;color:#ea580c">WELCOME10</p>
          </div>
          <p style="margin:0;color:#6b7280;font-size:13px">10% off · Vence en <strong>72 horas</strong></p>
          <p style="margin:32px 0 0"><a href="#" style="display:inline-block;background:#111;color:#fff;text-decoration:none;padding:14px 32px;border-radius:6px;font-weight:600">Comprar ahora</a></p>
        </td></tr>
      </table>
    `)
  }
  if (variant === 'magazine') {
    return shell(`
      <table cellpadding="0" cellspacing="0" border="0" align="center" width="600" style="margin:0 auto">
        <tr><td><img src="${HERO_IMAGE_BY_KIND.welcome}" width="600" style="display:block;width:100%" alt=""></td></tr>
        <tr><td style="padding:48px 32px;background:#fafafa">
          <p style="margin:0;font-size:11px;letter-spacing:8px;color:#9ca3af">EDITORIAL · MAYO 2026</p>
          <h1 style="margin:16px 0;font-family:Georgia,serif;font-size:48px;font-weight:400;line-height:1.1">Hola.<br/>Llegaste justo a tiempo.</h1>
          <p style="margin:0;color:#374151;font-size:17px;line-height:1.7">Esta no es una tienda más. Es una conversación con la pieza que te vas a poner mañana. Empezamos.</p>
          <p style="margin:32px 0 0"><a href="#" style="border-bottom:2px solid #111;color:#111;text-decoration:none;font-weight:600">Empezar a leer →</a></p>
        </td></tr>
      </table>
    `, { bg: '#fafafa' })
  }
  if (variant === 'community') {
    return shell(`
      <table cellpadding="0" cellspacing="0" border="0" align="center" width="540" style="margin:48px auto;padding:0 24px;text-align:center">
        <tr><td><h2 style="margin:0;font-size:28px">¡Bienvenido al gang! 🖤</h2>
          <p style="color:#6b7280;margin:12px 0 32px">Acá no solo vendemos ropa. Hablamos de música, arte y los drops que vienen.</p>
        </td></tr>
        <tr><td>
          <table cellpadding="0" cellspacing="0" width="100%"><tr>
            <td style="padding:8px;text-align:center;background:#f9fafb;border-radius:8px;width:33%"><p style="margin:0;font-size:32px">📷</p><p style="margin:8px 0 0;font-weight:700;font-size:14px">Instagram</p><p style="margin:4px 0;color:#6b7280;font-size:11px">@demo_studios</p></td>
            <td style="padding:8px;text-align:center;background:#f9fafb;border-radius:8px;width:33%"><p style="margin:0;font-size:32px">🎵</p><p style="margin:8px 0 0;font-weight:700;font-size:14px">TikTok</p><p style="margin:4px 0;color:#6b7280;font-size:11px">@demo_studios</p></td>
            <td style="padding:8px;text-align:center;background:#f9fafb;border-radius:8px;width:33%"><p style="margin:0;font-size:32px">💬</p><p style="margin:8px 0 0;font-weight:700;font-size:14px">Discord</p><p style="margin:4px 0;color:#6b7280;font-size:11px">2.4K miembros</p></td>
          </tr></table>
        </td></tr>
        <tr><td style="padding:32px 0 0"><a href="#" style="background:#111;color:#fff;text-decoration:none;padding:14px 32px;border-radius:6px;font-weight:600">Ver el drop</a></td></tr>
      </table>
    `)
  }
  return shell(`<p style="padding:32px">Variante "${variant}" no implementada todavía.</p>`)
}

function previewPhoneRequest(variant: string): string {
  if (variant === 'minimal') {
    return shell(`
      <table cellpadding="0" cellspacing="0" border="0" align="center" width="520" style="margin:48px auto;padding:0 24px">
        <tr><td><h2 style="margin:0">Necesitamos tu número 📞</h2>
          <p style="color:#6b7280;margin:12px 0 24px">Para poder enviar tu orden #ORD-MOXBTC7H, necesitamos un teléfono de contacto.</p>
          <a href="#" style="display:inline-block;background:#111;color:#fff;text-decoration:none;padding:14px 28px;border-radius:6px;font-weight:600">Confirmar teléfono</a>
        </td></tr>
      </table>
    `)
  }
  if (variant === 'urgency') {
    return shell(`
      <table cellpadding="0" cellspacing="0" border="0" align="center" width="600" style="margin:0 auto">
        <tr><td style="background:#fbbf24;padding:14px 24px;text-align:center;color:#78350f;font-weight:700">⚠ Tu orden está esperando</td></tr>
        <tr><td style="padding:32px 40px;background:#fff">
          <h2 style="margin:0;font-size:24px">Tu pedido está listo para salir</h2>
          <p style="color:#6b7280;margin:16px 0 24px">Pero necesitamos tu teléfono para que la transportadora pueda contactarte. Esto solo toma 30 segundos.</p>
          <a href="#" style="display:inline-block;background:#dc2626;color:#fff;text-decoration:none;padding:14px 28px;border-radius:6px;font-weight:700">Confirmar mi teléfono ahora</a>
          <p style="margin:24px 0 0;color:#9ca3af;font-size:12px">Si pasa una semana sin respuesta, retenemos el pedido y reembolsamos el pago.</p>
        </td></tr>
      </table>
    `)
  }
  return shell(`<p style="padding:32px">Variante "${variant}" no implementada todavía.</p>`)
}

function previewSizeInquiry(variant: string): string {
  if (variant === 'minimal') {
    return shell(`
      <table cellpadding="0" cellspacing="0" border="0" align="center" width="520" style="margin:48px auto;padding:0 24px">
        <tr><td><h2 style="margin:0">Necesitamos confirmar tu talla</h2>
          <p style="color:#6b7280;margin:12px 0 24px">Notamos que tu orden no tiene una talla asignada. Indícanos la talla correcta.</p>
          <a href="#" style="background:#111;color:#fff;text-decoration:none;padding:12px 24px;border-radius:6px;font-weight:600">Indicar talla</a>
        </td></tr>
      </table>
    `)
  }
  if (variant === 'visual') {
    return shell(`
      <table cellpadding="0" cellspacing="0" border="0" align="center" width="600" style="margin:0 auto;background:#fff">
        <tr><td style="padding:32px"><img src="https://picsum.photos/seed/sizechart/536/300" width="100%" style="display:block;border-radius:8px" alt=""></td></tr>
        <tr><td style="padding:0 32px">
          <h2 style="margin:0">Confirma tu talla</h2>
          <p style="color:#6b7280;margin:12px 0 24px">Tu Long Sleeve Somnolencia no tiene talla asignada. Estas son las medidas:</p>
          <table cellpadding="10" cellspacing="0" border="1" bordercolor="#e5e7eb" width="100%" style="border-collapse:collapse;font-size:13px">
            <tr style="background:#f9fafb"><th style="text-align:left">Talla</th><th>Pecho</th><th>Largo</th></tr>
            <tr><td>S</td><td>50 cm</td><td>68 cm</td></tr>
            <tr><td>M</td><td>54 cm</td><td>70 cm</td></tr>
            <tr><td>L</td><td>58 cm</td><td>72 cm</td></tr>
            <tr><td>XL</td><td>62 cm</td><td>74 cm</td></tr>
            <tr><td>XXL</td><td>66 cm</td><td>76 cm</td></tr>
          </table>
          <p style="margin:24px 0;text-align:center"><a href="#" style="background:#111;color:#fff;text-decoration:none;padding:14px 28px;border-radius:6px;font-weight:600">Indicar mi talla →</a></p>
        </td></tr>
      </table>
    `)
  }
  return shell(`<p style="padding:32px">Variante "${variant}" no implementada todavía.</p>`)
}

function previewAbandonedCart(variant: string): string {
  if (variant === 'subtle') {
    return shell(`
      <table cellpadding="0" cellspacing="0" border="0" align="center" width="540" style="margin:48px auto;padding:0 24px">
        <tr><td><h2 style="margin:0">¿Te quedaste pensando?</h2>
          <p style="color:#6b7280;margin:12px 0 24px">Dejaste el Long Sleeve "Somnolencia" en el carrito. Está esperándote.</p>
          ${PRODUCT_CARD}
          <p style="margin:8px 0 0;text-align:center"><a href="#" style="background:#111;color:#fff;text-decoration:none;padding:14px 28px;border-radius:6px;font-weight:600">Volver a mi carrito</a></p>
        </td></tr>
      </table>
    `)
  }
  if (variant === 'with-discount') {
    return shell(`
      <table cellpadding="0" cellspacing="0" border="0" align="center" width="600" style="margin:0 auto">
        <tr><td style="background:#dc2626;padding:32px;text-align:center;color:#fff">
          <h1 style="margin:0;font-size:30px">10% off — solo por hoy</h1>
          <p style="margin:8px 0 0;opacity:0.9">Para que termines lo que empezaste 🔥</p>
        </td></tr>
        <tr><td style="padding:32px;background:#fff">${PRODUCT_CARD}
          <div style="border:2px dashed #dc2626;padding:14px 24px;text-align:center;margin:24px 0">
            <p style="margin:0;font-size:11px;letter-spacing:2px;color:#dc2626;text-transform:uppercase">Código exclusivo</p>
            <p style="margin:8px 0 0;font-family:monospace;font-size:24px;font-weight:900;color:#dc2626;letter-spacing:4px">VOLVE10</p>
          </div>
          <p style="margin:0;text-align:center"><a href="#" style="background:#dc2626;color:#fff;text-decoration:none;padding:14px 32px;border-radius:6px;font-weight:700">Aplicar y comprar</a></p>
          <p style="margin:24px 0 0;text-align:center;color:#9ca3af;font-size:11px">Vence en 24 horas. Stock limitado.</p>
        </td></tr>
      </table>
    `)
  }
  if (variant === 'social-proof') {
    return shell(`
      <table cellpadding="0" cellspacing="0" border="0" align="center" width="600" style="margin:0 auto;background:#fff">
        <tr><td style="padding:32px 40px"><h2 style="margin:0">Otros también lo están viendo</h2>
          <p style="color:#6b7280;margin:8px 0 24px">⚡ <strong>14 personas</strong> miraron este producto en la última hora.</p>
          ${PRODUCT_CARD}
          <table cellpadding="14" cellspacing="0" width="100%" style="background:#f9fafb;border-radius:8px;margin-bottom:24px"><tr><td>
            <p style="margin:0;font-size:13px;color:#fbbf24">★★★★★</p>
            <p style="margin:8px 0 0;color:#374151">"Calidad brutal. El gramaje vale cada peso." <strong>— Andrés P.</strong></p>
          </td></tr></table>
          <table cellpadding="14" cellspacing="0" width="100%" style="background:#f9fafb;border-radius:8px;margin-bottom:24px"><tr><td>
            <p style="margin:0;font-size:13px;color:#fbbf24">★★★★★</p>
            <p style="margin:8px 0 0;color:#374151">"Buen fit, gramaje perfecto. Repetiría." <strong>— María O.</strong></p>
          </td></tr></table>
          <p style="margin:0;text-align:center"><a href="#" style="background:#111;color:#fff;text-decoration:none;padding:14px 28px;border-radius:6px;font-weight:600">Volver a mi carrito</a></p>
        </td></tr>
      </table>
    `)
  }
  return shell(`<p style="padding:32px">Variante "${variant}" no implementada todavía.</p>`)
}

function previewRestockAlert(variant: string): string {
  if (variant === 'minimal') {
    return shell(`
      <table cellpadding="0" cellspacing="0" border="0" align="center" width="540" style="margin:48px auto;padding:0 24px">
        <tr><td><h2 style="margin:0">🔔 Volvió el stock</h2>
          <p style="color:#6b7280;margin:12px 0 24px">El Long Sleeve "Somnolencia" en talla XXL volvió. Quedan pocas unidades.</p>
          <a href="#" style="background:#111;color:#fff;text-decoration:none;padding:14px 28px;border-radius:6px;font-weight:600">Comprar ahora</a>
        </td></tr>
      </table>
    `)
  }
  if (variant === 'product-hero') {
    return shell(`
      <table cellpadding="0" cellspacing="0" border="0" align="center" width="600" style="margin:0 auto;background:#fff">
        <tr><td style="position:relative">
          <img src="https://picsum.photos/seed/ls2/600/600" width="600" style="display:block;width:100%" alt="">
          <div style="position:absolute;top:24px;left:24px;background:#dc2626;color:#fff;padding:8px 14px;border-radius:4px;font-size:11px;letter-spacing:2px;font-weight:700">RESTOCK</div>
        </td></tr>
        <tr><td style="padding:32px 40px"><h2 style="margin:0;font-size:28px">Volvió en talla XXL</h2>
          <p style="color:#dc2626;margin:8px 0 24px;font-weight:700">⚡ Solo 6 unidades disponibles</p>
          <p style="margin:0;font-size:24px;font-weight:700">$ 240.000 COP</p>
          <p style="margin:24px 0 0"><a href="#" style="background:#111;color:#fff;text-decoration:none;padding:14px 32px;border-radius:6px;font-weight:600">Asegurar la mía</a></p>
        </td></tr>
      </table>
    `)
  }
  return shell(`<p style="padding:32px">Variante "${variant}" no implementada todavía.</p>`)
}

export function previewHtml(kind: string, variant: string): string {
  switch (kind) {
    case 'order_paid_customer': return previewOrderPaidCustomer(variant)
    case 'order_paid_admin':    return previewOrderPaidAdmin(variant)
    case 'order_shipped':       return previewOrderShipped(variant)
    case 'welcome':             return previewWelcome(variant)
    case 'phone_request':       return previewPhoneRequest(variant)
    case 'size_inquiry':        return previewSizeInquiry(variant)
    case 'abandoned_cart':      return previewAbandonedCart(variant)
    case 'restock_alert':       return previewRestockAlert(variant)
    default:
      return shell(`<p style="padding:32px">Plantilla "${kind}" no encontrada.</p>`)
  }
}
